/**
 * API Utility for Data Lagoon UI
 *
 * This module centralizes all API calls and token management for the Data Lagoon UI.
 * It uses MSAL for authentication and automatically acquires tokens before making API requests.
 * 
 * Usage:
 * 1. Call setMsal(instance, account) once after MSAL is initialized (e.g., in your app's root or a provider).
 * 2. Use exported API functions (e.g., getDatasetPreview) directly in your components.
 *    These functions will handle token acquisition and API calls internally.
 * 3. Ensure to handle errors appropriately in your components. Currently it is configured in Me.tsx.
 *
 * Example:
 *   import { setMsal, getDatasetPreview } from 'src/api/gates/API';
 *   setMsal(instance, account);
 *   const data = await getDatasetPreview(datasetId);
 */

import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { protectedResources } from 'src/authConfig';
import type { Dataset, DatasetCollection } from 'src/sections/datasets/types';

// Internal MSAL instance and account references
let msalInstance: IPublicClientApplication | null = null;
let msalAccount: AccountInfo | null = null;

/**
 * Initializes the MSAL instance and account for API calls.
 * Call this once after MSAL is ready.
 * @param msal - The MSAL public client application instance
 * @param account - The MSAL account info for the current user
 */
export function setMsal(msal: IPublicClientApplication, account: AccountInfo) {
  msalInstance = msal;
  msalAccount = account;
}

/**
 * Acquires an access token silently using the stored MSAL instance and account.
 * Falls back to popup if silent acquisition fails due to token expiration.
 * @returns The access token as a string
 * @throws If MSAL is not initialized
 */
async function getToken() {
  if (!msalInstance || !msalAccount) throw new Error('MSAL not initialized');
  
  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: protectedResources.dataLagoonApi.scopes,
      account: msalAccount,
      forceRefresh: false,
    });
    return response.accessToken;
  } catch (error: any) {
    // Check if interaction is required (token expired or refresh failed)
    const isInteractionRequired = 
      error?.errorCode === 'interaction_required' ||
      error?.errorCode === 'consent_required' ||
      error?.errorCode === 'login_required' ||
      error?.errorCode === 'token_renewal_error';
    
    if (isInteractionRequired) {
      console.log('Token expired or interaction required, attempting popup...');
      try {
        const response = await msalInstance.acquireTokenPopup({
          scopes: protectedResources.dataLagoonApi.scopes,
          prompt: 'select_account',
        });
        return response.accessToken;
      } catch (popupError) {
        console.error('Popup token acquisition failed:', popupError);
        throw popupError;
      }
    }
    throw error;
  }
}

// 1. Knowledge Gains
export async function get_knowledge_gains_math_matrix(tagSections: string, segment: string, district?: string) {
  const token = await getToken();
  const params = new URLSearchParams({
    tagSections,
    segment
  });

  if (district) {
    params.append('district', district);
  }

  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/mathmatrix/knowledge-gains?${params}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) throw new Error('Failed to fetch knowledge gains data');
  return response.json();
}

// 2. Enrollment Completion
export async function get_enrollment_completion_math_matrix(district?: string) {
  const token = await getToken();
  const params = new URLSearchParams();

  if (district) {
    params.append('district', district);
  }

  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/mathmatrix/enrollment-completion?${params}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) throw new Error('Failed to fetch enrollment completion data');
  return response.json();
}

// 3. Satisfaction Ratings
export async function get_satisfaction_ratings_math_matrix(tagSections: string, segment: string, district?: string) {
  const token = await getToken();
  const params = new URLSearchParams({
    tagSections,
    segment
  });

  if (district) {
    params.append('district', district);
  }

  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/mathmatrix/satisfaction-ratings?${params}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) throw new Error('Failed to fetch satisfaction ratings data');
  return response.json();
}

// 4. Roster
export async function get_roster_math_matrix(district?: string) {
  const token = await getToken();
  const params = new URLSearchParams();

  if (district) {
    params.append('district', district);
  }

  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/mathmatrix/roster?${params}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) throw new Error('Failed to fetch roster data');
  return response.json();
}

// 5. Admin - Get Districts List
export async function get_admin_districts_math_matrix() {
  const token = await getToken();

  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/mathmatrix/admin/districts`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) throw new Error('Failed to fetch districts list');
  return response.json();
}

/**
 * Fetches preview data for a given dataset.
 * Handles token acquisition automatically.
 * @param datasetId - The ID of the dataset to preview
 * @returns The preview data as JSON
 * @throws If the API call fails or MSAL is not initialized
 */
export async function getDatasetPreview(datasetId: string) {
  const token = await getToken();
  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/datasets/${datasetId}/preview-data`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  if (!response.ok) throw new Error('Failed to fetch preview data');
  return response.json();
}

export async function getData_Dictionary(fileName: string) {
  const token = await getToken();
  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/datasets/url?fileName=${encodeURIComponent(fileName)}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  if (!response.ok) throw new Error('Failed to fetch data dictionary');
  return response.json();
}
export async function getUserExtended(userId: string) {
  const token = await getToken();
  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/users/${userId}/extended`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = new Error('Failed to fetch user data');
    error.name = 'FetchError';
    throw error;
  }

  return response.json();
}

/**
 * Submits a bulk approval request for file access.
 * @param formData - The request payload as FormData (supports file upload and complex fields)
 * @returns The API response as JSON
 * @throws If the API call fails or MSAL is not initialized
 */
export async function submitBulkApprovalRequest(formData: FormData) {
  const token = await getToken();
  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/approvalrequests/files/bulk`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );
  if (!response.ok) throw new Error('Failed to submit bulk approval request');
  return response.json();
}

/**
 * Gets the metadata for a specific approval request.
 * @param requestId - The ID of the approval request
 * @returns The request metadata
 * @throws If the API call fails or MSAL is not initialized
 */
export async function getRequestMetadata(requestId: string) {
  const token = await getToken();
  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/approvalrequests/metadata/${requestId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (!response.ok) throw new Error('Failed to fetch request metadata');
  return response.json();
}

/**
 * Submits a request access form.
 * @param payload - The request access form data containing only organization
 * @returns The API response as JSON
 * @throws If the API call fails or MSAL is not initialized
 */
export async function requestAccess(payload: {
  organization?: string;
}) {
  const token = await getToken();
  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/users/request-access`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        OrganizationName: payload.organization?.trim() || null,
      }),
    }
  );
  if (!response.ok) throw new Error('Failed to submit request access');
  return response.json();
}

/**
 * Fetches all dataset collections with their associated files.
 * @returns The collections data as JSON
 * @throws If the API call fails or MSAL is not initialized
 */
export async function getDatasetCollections() {
  const token = await getToken();
  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/datasets/collections-with-files`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (response.status === 403) {
    const error = new Error('Unauthorised');
    error.name = 'UnauthorisedError';
    throw error;
  }

  if (!response.ok) {
    const error = new Error('Failed to fetch dataset collections');
    error.name = 'FetchError';
    throw error;
  }

  return response.json();
}

/**
 * Fetches dataset collections filtered to only include approved datasets.
 * This reduces the need for repeated filtering in UI components.
 * @returns The filtered collections data with only approved datasets
 * @throws If the API call fails or MSAL is not initialized
 */
export async function getApprovedDatasetCollections(): Promise<DatasetCollection[]> {
  const collections: DatasetCollection[] = await getDatasetCollections();

  // Filter collections to only include those with approved datasets
  return collections
    .map((collection: DatasetCollection) => ({
      ...collection,
      datasets: collection.datasets.filter((dataset: Dataset) => dataset.approvalStatus === 'Approved')
    }))
    .filter((collection: DatasetCollection) => collection.datasets.length > 0);
}

/**
 * Gets download URIs for specified dataset IDs.
 * @param datasetIds - Array of dataset IDs to get download URLs for
 * @returns Array of download URL objects
 * @throws If the API call fails or MSAL is not initialized
 */
export async function getDownloadURIs(datasetIds: string[]) {
  const token = await getToken();
  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/datasets/download/stream`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ datasetIds }),
    }
  );

  if (response.status === 403) {
    const error = new Error('Unauthorized');
    error.name = 'UnauthorizedError';
    throw error;
  }

  if (!response.ok) {
    const error = new Error('Failed to get download URIs');
    error.name = 'FetchError';
    throw error;
  }

  return response.json();
}

/**
 * Updates the approval status of a user.
 * @param userId - The user ID
 * @param GatesApprovalStatusID - The new approval status ID
 * @returns The API response as JSON
 * @throws If the API call fails or MSAL is not initialized
 */
export async function updateUserApprovalStatus(userId: string, GatesApprovalStatusID: number) {
  const token = await getToken();
  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/users/update-approval-status`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, GatesApprovalStatusID }),
    }
  );
  if (!response.ok) throw new Error('Failed to update approval status');
  return response.json();
}


/**
 * Gets metadata documents for a specific user.
 * @param userId - The ID of the user to get documents for
 * @returns Array of metadata documents
 * @throws If the API call fails or MSAL is not initialized
 */
export async function getMetadataDocuments(userId: string) {
  const token = await getToken();
  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/approvalrequests/metadatadocuments/${userId}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (response.status === 403) {
    const error = new Error('Unauthorized');
    error.name = 'UnauthorizedError';
    throw error;
  }

  if (!response.ok) {
    const error = new Error('Failed to fetch metadata documents');
    error.name = 'FetchError';
    throw error;
  }

  return response.json();
}

/**
 * Uploads additional documents for a specific user.
 * @param userId - The ID of the user to upload documents for
 * @param formData - FormData containing the documents to upload
 * @returns The API response as JSON
 * @throws If the API call fails or MSAL is not initialized
 */
export async function uploadAdditionalDocuments(userId: string, formData: FormData) {
  const token = await getToken();
  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/users/${userId}/additional-documents`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const error = new Error('Failed to upload additional documents');
    error.name = 'UploadError';
    throw error;
  }

  return response.json();
}

/**
 * Gets additional documents for a specific user.
 * @param userId - The ID of the user to get additional documents for
 * @returns Array of additional document URLs
 * @throws If the API call fails or MSAL is not initialized
 */
export async function getAdditionalDocuments(userId: string) {
  const token = await getToken();
  const response = await fetch(
    `${protectedResources.dataLagoonApi.endpoint}/users/${userId}/additional-documents`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (response.status === 403) {
    const error = new Error('Unauthorized');
    error.name = 'UnauthorizedError';
    throw error;
  }

  if (!response.ok) {
    const error = new Error('Failed to fetch additional documents');
    error.name = 'FetchError';
    throw error;
  }

  return response.json();
}